import Box from "@mui/material/Box";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import type { RecurringJob } from "../../../api/client.ts";
import { formatNumber } from "../../../utils/format.ts";
import ScopeToggle from "../../filters/ScopeToggle.tsx";
import Panel from "../../Panel.tsx";
import RelativeTime from "../../RelativeTime.tsx";
import Sparkline from "../../Sparkline.tsx";
import { StatusLabel } from "../../StatusIcon.tsx";
import ScrollTable from "../../table/ScrollTable.tsx";
import JobName from "./JobName.tsx";
import { NEEDS_ATTENTION, cadenceLabel, jobTitle, recurringStatus, seconds, timeUntil } from "./recurringFormat.ts";

type Scope = "all" | "attention";

const num = { fontVariantNumeric: "tabular-nums" } as const;

/**
 * Every recurring job (a periodic Restate object): is it on and on time, when
 * did it last run and when does it run next, how often it ran in the last day
 * and how long a run takes. A row opens the job's drawer.
 */
export default function RecurringJobsPanel({
  jobs,
  onOpen,
}: {
  jobs: RecurringJob[];
  onOpen: (name: string) => void;
}) {
  const attention = jobs.filter((j) => NEEDS_ATTENTION.includes(j.status));
  const [scope, setScope] = useState<Scope>("all");
  const shown = scope === "attention" ? attention : jobs;

  return (
    <Panel
      title="Recurring jobs"
      subtitle="Each job's last and next run, and how often it ran in the last 24 hours"
      count={jobs.length}
      disablePadding
      action={
        <ScopeToggle<Scope>
          value={scope}
          onChange={setScope}
          aria-label="Which jobs"
          options={[
            { value: "all", label: "All" },
            { value: "attention", label: "Needs attention", badge: attention.length },
          ]}
        />
      }
    >
      {shown.length === 0 ? (
        <Typography variant="body2" sx={{ color: "text.secondary", px: 2, py: 3, textAlign: "center" }}>
          {scope === "attention" ? "Every job is on time" : "No recurring jobs"}
        </Typography>
      ) : (
        <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Job</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last run</TableCell>
              <TableCell>Next run</TableCell>
              <TableCell align="right">Runs, 24h</TableCell>
              <TableCell align="right">Failed</TableCell>
              <TableCell align="right">Typical run</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((job) => (
              <JobRow key={job.name} job={job} onOpen={() => onOpen(job.name)} />
            ))}
          </TableBody>
        </ScrollTable>
      )}
    </Panel>
  );
}

function JobRow({ job, onOpen }: { job: RecurringJob; onOpen: () => void }) {
  const status = recurringStatus(job.status);
  const why = job.chains.find((c) => c.status === job.status && c.last_error)?.last_error;
  const lastDay = job.hourly.slice(-24);
  const chains = job.chains.filter((c) => c.status !== "stopped").length;
  return (
    <TableRow hover onClick={onOpen} sx={{ cursor: "pointer" }}>
      <TableCell>
        <Typography variant="body2" component="div" sx={{ fontWeight: 500, display: "flex" }}>
          <JobName country={job.country}>{jobTitle(job)}</JobName>
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {job.category_label} · {cadenceLabel(job)}
          {job.keyed ? ` · ${formatNumber(chains)} ${chains === 1 ? "chain" : "chains"}` : ""}
        </Typography>
      </TableCell>
      <TableCell>
        <Tooltip title={why ?? ""} disableHoverListener={!why}>
          <Box component="span">
            <StatusLabel kind={status.kind} label={status.label} />
          </Box>
        </Tooltip>
        {job.running > 0 && (
          <Typography variant="caption" component="div" sx={{ color: "text.secondary" }}>
            {job.keyed ? `${formatNumber(job.running)} running` : "Running now"}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <RelativeTime value={job.last_run_at} />
      </TableCell>
      <TableCell sx={{ color: job.status === "late" ? "warning.main" : undefined }}>
        {!job.switch_on ? "–" : !job.next_run_at && job.running > 0 ? "After this run" : timeUntil(job.next_run_at)}
      </TableCell>
      <TableCell align="right" sx={num}>
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
          <Sparkline values={lastDay.map((h) => h.ran + h.failed)} width={72} height={18} showRange={false} />
          <span>{formatNumber(job.runs_24h)}</span>
        </Box>
      </TableCell>
      <TableCell align="right" sx={{ ...num, color: job.failures_24h ? "error.main" : "text.secondary" }}>
        {job.failures_24h ? formatNumber(job.failures_24h) : "–"}
      </TableCell>
      <TableCell align="right" sx={num}>
        <Tooltip title={job.p95_s != null ? `95% of runs took up to ${seconds(job.p95_s)}` : ""}>
          <span>{seconds(job.p50_s)}</span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}
